import { useCallback, useEffect, useState } from 'react'
import PropTypes from 'prop-types'
import { useTranslation } from 'react-i18next'

import http from '../../shared/http'

import styles from './style.module.scss'

const IMAGE_NAME = /\.(png|jpe?g|gif|webp|bmp|svg)$/i
const PDF_NAME = /\.pdf$/i

/**
 * Erkennt, was zu zeigen ist.
 *
 * Zuerst die Angabe des Servers, dann die Dateiendung: Manche Ablagen liefern
 * `application/octet-stream` für alles — dann wäre eine erkennbare PDF-Datei
 * ohne den zweiten Blick nicht darstellbar.
 */
function kindOf(mime, name = '') {
	if (mime && mime.startsWith('image/')) return 'image'
	if (mime === 'application/pdf') return 'pdf'
	if (IMAGE_NAME.test(name)) return 'image'
	if (PDF_NAME.test(name)) return 'pdf'
	return 'other'
}

/**
 * Vergrößerte Vorschau einer Datei, mit Blättern durch die Liste dahinter.
 *
 * Gedacht für die Dokumentenlisten: Wer prüft, ob eine Elternbestätigung
 * lesbar hochgeladen wurde, will nicht zehn Dateien herunterladen, einzeln
 * öffnen und wieder schließen. Deshalb öffnet ein Klick auf die Zeile die
 * Datei hier — und die Pfeiltasten führen zur nächsten, ohne die Ansicht zu
 * verlassen.
 *
 * Die Dateien liegen hinter der angemeldeten Schnittstelle; ein `src` direkt
 * auf die Adresse schickt keinen Anmelde-Kopfeintrag mit und liefe ins Leere.
 * Deshalb wird die Datei geholt und als lokale Adresse eingesetzt — und beim
 * Wechsel wieder freigegeben, sonst sammelt sich das Geblätterte im Speicher.
 */
export default function FilePreview(props) {
	const { items, index, onIndexChange, onClose, onDownload, actions } = props
	const { t } = useTranslation()
	const [file, setFile] = useState({ status: 'loading' })

	const total = items.length
	const current = items[index]
	const url = current?.url

	const step = useCallback(
		delta => {
			if (total < 2) return
			// Umlaufend: Nach der letzten Datei kommt wieder die erste.
			onIndexChange((index + delta + total) % total)
		},
		[index, total, onIndexChange]
	)

	useEffect(() => {
		const onKey = event => {
			if (event.key === 'Escape') onClose()
			if (event.key === 'ArrowRight') step(1)
			if (event.key === 'ArrowLeft') step(-1)
		}

		document.addEventListener('keydown', onKey)
		const previousOverflow = document.body.style.overflow
		document.body.style.overflow = 'hidden'

		return () => {
			document.removeEventListener('keydown', onKey)
			document.body.style.overflow = previousOverflow
		}
	}, [onClose, step])

	useEffect(() => {
		if (!url) return undefined

		let cancelled = false
		let objectUrl = null
		setFile({ status: 'loading' })

		http.get(url, { responseType: 'blob', disableToast: true })
			.then(({ data }) => {
				if (cancelled) return
				objectUrl = URL.createObjectURL(data)
				setFile({ status: 'ready', url: objectUrl, mime: data.type })
			})
			.catch(() => {
				if (!cancelled) setFile({ status: 'error' })
			})

		return () => {
			cancelled = true
			if (objectUrl) URL.revokeObjectURL(objectUrl)
		}
	}, [url])

	if (!current) return null

	const kind = file.status === 'ready' ? kindOf(file.mime, current.name) : null

	return (
		<div
			className={styles.backdrop}
			role="dialog"
			aria-modal="true"
			aria-label={t('Preview')}
			// Klick auf den Hintergrund schließt. Klicks aus dem Inhalt heraus
			// steigen bis hierher auf — deshalb die Prüfung auf das Ziel.
			onClick={event => {
				if (event.target === event.currentTarget) onClose()
			}}
		>
			<div className={styles.head}>
				<div className={styles.titles}>
					<span className={styles.name}>{current.name}</span>
					{current.meta && <span className={styles.meta}>{current.meta}</span>}
				</div>
				<div className={styles.headActions}>
					{actions.map((action, index) => (
						<button
							key={index}
							type="button"
							className={styles.action}
							disabled={action.disabled}
							onClick={() => action.onClick(current)}
						>
							{action.label}
						</button>
					))}
					{onDownload && (
						<button type="button" className={styles.action} onClick={() => onDownload(current)}>
							{t('Download')}
						</button>
					)}
					<button type="button" className={styles.close} onClick={onClose} aria-label={t('Close')}>
						×
					</button>
				</div>
			</div>

			{total > 1 && (
				<button
					type="button"
					className={`${styles.nav} ${styles.prev}`}
					onClick={() => step(-1)}
					aria-label={t('Previous file')}
				>
					‹
				</button>
			)}

			<div className={styles.stage}>
				{file.status === 'loading' && <p className={styles.note}>{t('Loading')}</p>}

				{file.status === 'error' && <p className={styles.note}>{t('The file could not be opened.')}</p>}

				{kind === 'image' && <img className={styles.image} src={file.url} alt={current.name} />}

				{kind === 'pdf' && <iframe className={styles.frame} src={file.url} title={current.name} />}

				{kind === 'other' && (
					<div className={styles.note}>
						<p>{t('This file type cannot be shown here.')}</p>
						{onDownload && (
							<button type="button" className={styles.action} onClick={() => onDownload(current)}>
								{t('Download')}
							</button>
						)}
					</div>
				)}
			</div>

			{total > 1 && (
				<button
					type="button"
					className={`${styles.nav} ${styles.next}`}
					onClick={() => step(1)}
					aria-label={t('Next file')}
				>
					›
				</button>
			)}

			{total > 1 && (
				<div className={styles.counter}>
					{index + 1} / {total}
					<span className={styles.hint}>{t('Use the arrow keys to page through.')}</span>
				</div>
			)}
		</div>
	)
}

FilePreview.propTypes = {
	/** Die Dateien der Liste, in der Reihenfolge der Ansicht. */
	items: PropTypes.arrayOf(
		PropTypes.shape({
			/** Anzeigename inkl. Endung — die Endung entscheidet mit über die Darstellung. */
			name: PropTypes.string.isRequired,
			/** Adresse der angemeldeten Schnittstelle, von der die Datei kommt. */
			url: PropTypes.string.isRequired,
			/** Zweite Zeile im Kopf, etwa Firma und Datum. */
			meta: PropTypes.string
		})
	).isRequired,
	index: PropTypes.number.isRequired,
	onIndexChange: PropTypes.func.isRequired,
	onClose: PropTypes.func.isRequired,
	onDownload: PropTypes.func,
	/**
	 * Handlungen zum gezeigten Datensatz — dieselben, die in der Liste im
	 * Drei-Punkte-Menü stehen. Bekommen den Eintrag übergeben.
	 */
	actions: PropTypes.arrayOf(
		PropTypes.shape({
			label: PropTypes.string.isRequired,
			onClick: PropTypes.func.isRequired,
			disabled: PropTypes.bool
		})
	)
}

FilePreview.defaultProps = {
	actions: []
}
